import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'
import { Select } from '@factorialco/f0-react/dist/experimental'
import { Pin } from '@factorialco/f0-react/icons/app'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { SchemaType } from 'modules/shift_management/lib/forms/GenerateCoverageForm'
import { useSelectableLocations } from 'modules/shift_management/lib/useSelectableLocations'

import { DateRange } from './DateRange'
import { GenerationMethod } from './GenerationMethod'
import { ReferencePeriod } from './ReferencePeriod'

interface ReferencePeriodAlert {
  value: boolean
  loading: boolean
}

interface Props {
  setSubmitDisabled: (value: boolean) => void
  currentLocation: string
}

const Content = observer(({ currentLocation, setSubmitDisabled }: Props) => {
  const [dateRangeAlert, setDateRangeAlert] = useState<boolean>(false)
  const [referencePeriodAlert, setReferencePeriodAlert] = useState<boolean>(false)
  const [shiftsAlert, setShiftsAlert] = useState<ReferencePeriodAlert>({
    value: false,
    loading: false,
  })
  const [coverageAlert, setCoverageAlert] = useState<ReferencePeriodAlert>({
    value: false,
    loading: false,
  })
  const form = useFormContext<SchemaType>()
  const { watch } = form
  const { employee } = useCurrentSession()
  const employeeId = employee?.id
  const { loading: locationsLoading, locations } = useSelectableLocations(employeeId, false)

  const [generationMethod, locationId, originalStartAt, targetStartAt] = watch([
    'generationMethod',
    'locationIds',
    'originalStartAt',
    'targetStartAt',
  ])

  useEffect(() => {
    if (!currentLocation) return

    form.setValue('locationIds', [Number(currentLocation)])
  }, [currentLocation])

  return (
    <Box flexDirection="column" gap="s24" padding="s20">
      <Text size="200" className="text-f1-foreground-secondary">
        {i18n.t('shift_management.coverage_section.generate_coverage_modal.description')}
      </Text>
      <Box gap="s12">
        <Text weight="semibold" size="200" className="text-f1-foreground">
          1. {i18n.t('shift_management.coverage_section.generate_coverage_modal.workplace_field')}
        </Text>
        <Box gap="s12" flexDirection="row" width="full">
          <Box flexDirection="column" gap="s12" flexGrow alignItems="flexStart">
            <Box flexDirection="row" alignItems="center">
              <Text size="200" className="text-f1-foreground-secondary">
                {i18n.t('shift_management.coverage_section.generate_coverage_modal.workplace')}
              </Text>
              <Text size="200" className="text-f1-foreground-accent">
                *
              </Text>
            </Box>
            <Box width="full">
              <Select
                options={locations.map((location) => ({
                  label: location.name,
                  value: location.id.toString(),
                }))}
                value={locationId?.at(0)?.toString() || currentLocation.toString()}
                onChange={(value: string) => {
                  form.setValue('locationIds', [Number(value)])
                }}
                size="md"
                loading={locationsLoading}
                icon={Pin}
                hideLabel
                label={i18n.t(
                  'shift_management.coverage_section.generate_coverage_modal.workplace'
                )}
                showSearchBox
              />
            </Box>
          </Box>
          <Box flexDirection="column" gap="s12" alignItems="flexStart" flexGrow>
            <Box flexDirection="row" alignItems="center">
              <Text size="200" className="text-f1-foreground-secondary">
                {i18n.t('shift_management.coverage_section.generate_coverage_modal.date_range')}
              </Text>
              <Text size="200" className="text-f1-foreground-accent">
                *
              </Text>
            </Box>
            <Box width="full">
              <DateRange
                targetStartAt={targetStartAt}
                form={form}
                setAlert={(value) => setDateRangeAlert(value)}
              />
            </Box>
          </Box>
        </Box>
        {dateRangeAlert && (
          <Alert color="warning">
            <Box flexDirection="column" gap="s4">
              <Text size="200" weight="medium">
                {i18n.t(
                  'shift_management.coverage_section.generate_coverage_modal.date_range_alert.title'
                )}
              </Text>
              <Text size="200">
                {i18n.t(
                  'shift_management.coverage_section.generate_coverage_modal.date_range_alert.description'
                )}
              </Text>
            </Box>
          </Alert>
        )}
      </Box>

      <Box gap="s12">
        <Text weight="semibold" size="200" className="text-f1-foreground">
          2.{' '}
          {i18n.t(
            'shift_management.coverage_section.generate_coverage_modal.reference_period_field'
          )}
        </Text>
        <Box flexDirection="column" gap="s12" alignItems="flexStart" flexGrow>
          <Box width="full">
            <ReferencePeriod
              originalStartAt={originalStartAt}
              form={form}
              setAlert={(value) => setReferencePeriodAlert(value)}
              setShiftsAlert={setShiftsAlert}
              setCoverageAlert={setCoverageAlert}
              shiftsAlert={shiftsAlert}
              coverageAlert={coverageAlert}
              setSubmitDisabled={setSubmitDisabled}
            />
          </Box>
        </Box>
      </Box>
      {referencePeriodAlert && (
        <Alert color="error">
          <Box flexDirection="column" gap="s4">
            <Text size="200" weight="medium">
              {i18n.t(
                'shift_management.coverage_section.generate_coverage_modal.reference_period_alert.title'
              )}
            </Text>
            <Text size="200">
              {i18n.t(
                'shift_management.coverage_section.generate_coverage_modal.reference_period_alert.description'
              )}
            </Text>
          </Box>
        </Alert>
      )}
      <Box gap="s12">
        <Text size="200" className="text-f1-foreground" weight="semibold">
          3.{' '}
          {i18n.t(
            'shift_management.coverage_section.generate_coverage_modal.generation_method_field'
          )}
        </Text>
        <Box flexDirection="column" gap="s12" alignItems="flexStart" flexGrow>
          <Box width="full">
            <GenerationMethod
              generationMethod={generationMethod}
              form={form}
              disabled={referencePeriodAlert}
              shiftsAlert={shiftsAlert.value}
              coverageAlert={coverageAlert.value}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
})

export { Content }
